import React from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Chip,
    CircularProgress,
    Button,
} from "@mui/material";
import {
    NoteAlt as WorkOrderIcon,
    ChevronRight as ChevronRightIcon,
    Archive as ArchiveIcon,
    Unarchive as UnarchiveIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { WO_STATUS_META } from "./statusMeta";

function canArchive(wo) {
    return (
        wo.status === "complete" &&
        (Boolean(wo.project_detail) || Number(wo.received_qty || 0) >= Number(wo.quantity || 0))
    );
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
        return dateStr;
    }
}

export default function WorkOrdersList({
    workOrders,
    loading,
    hasMore,
    onLoadMore,
    onView,
    showArchived = false,
    onArchive,
    onRestore,
}) {
    if (loading && workOrders.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (workOrders.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <WorkOrderIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">
                    {showArchived ? "No archived work orders found." : "No work orders found."}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {workOrders.map((wo) => {
                const statusMeta = WO_STATUS_META[wo.status] || {
                    label: wo.status,
                    color: "default",
                };
                return (
                    <Card
                        key={wo.id}
                        elevation={0}
                        sx={{
                            border: 1,
                            borderColor: "primary.main",
                            boxShadow: "none",
                        }}
                    >
                        <CardActionArea onClick={() => onView(wo)} sx={{ display: "flex", alignItems: "stretch" }}>
                            <CardContent sx={{ py: 1.5, flex: 1, minWidth: 0, "&:last-child": { pb: 1.5 } }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                                        >
                                            {wo.wo_number}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {wo.part_display || "—"}
                                            {wo.part_description ? ` · ${wo.part_description}` : ""}
                                        </Typography>
                                        {wo.project_detail && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {wo.project_detail.number} · {wo.project_detail.name}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip label={statusMeta.label} size="small" color={statusMeta.color} />
                                </Box>
                                <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
                                    <Chip
                                        label={`Qty ${wo.quantity ?? "—"}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`${wo.travelers_complete_count ?? 0}/${wo.traveler_count ?? 0} steps`}
                                        size="small"
                                        variant="outlined"
                                        color={
                                            wo.traveler_count > 0 &&
                                            wo.travelers_complete_count === wo.traveler_count
                                                ? "success"
                                                : "default"
                                        }
                                    />
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {formatDate(wo.planned_start)} – {formatDate(wo.planned_finish)}
                                </Typography>
                            </CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    pr: 1,
                                    flexShrink: 0,
                                    color: "action.active",
                                }}
                            >
                                <ChevronRightIcon />
                            </Box>
                        </CardActionArea>
                        {showArchived ? (
                            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1.5, pb: 1 }}>
                                <Button
                                    size="small"
                                    color="primary"
                                    startIcon={<UnarchiveIcon fontSize="small" />}
                                    onClick={() => onRestore(wo)}
                                >
                                    Restore
                                </Button>
                            </Box>
                        ) : (
                            canArchive(wo) && (
                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 1.5, pb: 1 }}>
                                    <Button
                                        size="small"
                                        color="warning"
                                        startIcon={<ArchiveIcon fontSize="small" />}
                                        onClick={() => onArchive(wo)}
                                    >
                                        Archive
                                    </Button>
                                </Box>
                            )
                        )}
                    </Card>
                );
            })}
            {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                    <Button onClick={onLoadMore} disabled={loading} size="small">
                        {loading ? "Loading…" : "Load more"}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
